import { v4 as uuidv4 } from "uuid";

// `campaignConfig` is `z.any()` in the list schema; the `name` key holds the list name (a string),
// every other key is a campaign object. These resolvers only read the campaign `name`.
type LooseCampaignNameConfig = Record<string, { name?: string } | string | undefined>;

/** First campaign uuid in the config (its keys minus the `name` list-name key). */
const firstCampaignUuid = (campaignConfig: LooseCampaignNameConfig | null | undefined): string =>
	Object.keys(campaignConfig ?? {}).filter((key) => key !== "name")[0];

/**
 * The campaign name to pre-select on load: the `campaign` query param, else the first/default
 * campaign's name, else a generic fallback.
 */
export const getCampaignNameFromUrl = (
	search: string,
	campaignConfig: LooseCampaignNameConfig | null | undefined
): string => {
	const fallback = campaignConfig?.[firstCampaignUuid(campaignConfig)];

	return (
		new URLSearchParams(search).get("campaign") ||
		(typeof fallback === "object" ? fallback?.name : undefined) ||
		"Default Campaign"
	);
};

/**
 * The campaign uuid to pre-select on load: the `campaignUuid` query param, else the first campaign
 * uuid, else a fresh uuid (a brand-new default campaign).
 */
export const getCampaignUuidFromUrl = (
	search: string,
	campaignConfig: LooseCampaignNameConfig | null | undefined
): string =>
	new URLSearchParams(search).get("campaignUuid") || firstCampaignUuid(campaignConfig) || uuidv4();

/**
 * The campaign builder (CampaignOverview) must only mount once the selected campaign actually
 * exists in the freshest `campaignConfig`. Right after creating a campaign, `updateList` only
 * invalidates the List cache — the refetch that brings the new campaign into local state lands a
 * tick later. Gating on a truthy `campaignConfig` alone would mount CampaignPage against the
 * stale config (missing the new uuid), leaving it uninitialised and the canvas frozen until a
 * hard refresh. Requiring the uuid to be present closes that race.
 */
export const shouldShowCampaignBuilder = (
	isModalOpened: boolean,
	// `campaignConfig` is `z.any()` in the list schema, so it arrives loosely typed here.
	campaignConfig: Record<string, unknown> | null | undefined,
	currentlySelectedCampaignUuid: string
): boolean => isModalOpened && Boolean(campaignConfig?.[currentlySelectedCampaignUuid]);

// ── "Apply event end date" → campaign stop date (PREC-3520 / PREC-3540) ──

/** The fields of a campaign we read when applying the event end date as its stop date. */
type CampaignStopDateFields = { name?: string; stopCampaignAt?: string | null };

// `campaignConfig` is `z.any()` in the list schema; its `name` key holds the list name (a string),
// every other key is a campaign object. We only touch the bits relevant to the stop date here.
type LooseCampaignConfig = Record<string, CampaignStopDateFields | string | undefined>;

/** A campaign whose existing stop date would be overwritten by "Apply event end date". */
export interface StopDateOverwrite {
	uuid: string;
	name: string;
	currentStopCampaignAt: string;
}

/** True when two dates fall on the same calendar day — tells an already-applied date from a manual one. */
const isSameDay = (a: string, b: string): boolean => {
	const da = new Date(a);
	const db = new Date(b);

	return (
		!Number.isNaN(da.getTime()) &&
		!Number.isNaN(db.getTime()) &&
		da.toDateString() === db.toDateString()
	);
};

const isCampaignObject = (
	value: CampaignStopDateFields | string | undefined
): value is CampaignStopDateFields => Boolean(value) && typeof value === "object";

/**
 * Campaigns whose existing stop date differs from the event end date — i.e. the ones an "Apply
 * event end date" would overwrite. When this is empty the apply is non-destructive (only fills
 * campaigns with no stop date) and needs no confirmation; otherwise we ask first (PREC-3540).
 */
export const computeStopDateOverwrites = (
	campaignConfig: LooseCampaignConfig | null | undefined,
	endDate: string
): StopDateOverwrite[] => {
	if (!campaignConfig) {
		return [];
	}

	return Object.keys(campaignConfig)
		.filter((key) => key !== "name")
		.reduce<StopDateOverwrite[]>((acc, uuid) => {
			const campaign = campaignConfig[uuid];

			if (
				isCampaignObject(campaign) &&
				campaign.stopCampaignAt &&
				!isSameDay(campaign.stopCampaignAt, endDate)
			) {
				acc.push({
					uuid,
					name: campaign.name ?? "",
					currentStopCampaignAt: campaign.stopCampaignAt,
				});
			}

			return acc;
		}, []);
};

/**
 * Build the campaignConfig to persist when applying the event end date as the campaign stop date.
 * `overwriteExisting = false` fills only campaigns that have no stop date (the silent path);
 * `overwriteExisting = true` overwrites every campaign (after the user confirms) (PREC-3540).
 */
export const buildStopDateConfig = (
	campaignConfig: LooseCampaignConfig,
	endDate: string,
	overwriteExisting: boolean
): Record<string, unknown> => {
	const updated: Record<string, unknown> = { ...campaignConfig };

	Object.keys(campaignConfig)
		.filter((key) => key !== "name")
		.forEach((uuid) => {
			const campaign = campaignConfig[uuid];

			if (!isCampaignObject(campaign)) {
				return;
			}

			if (overwriteExisting || !campaign.stopCampaignAt) {
				updated[uuid] = { ...campaign, stopCampaignAt: endDate };
			}
		});

	return updated;
};
